/**
 * First-party browser error reporting for the LIFF and admin apps.
 *
 * Both paths are served from the app's OWN origin by the `(errors)` snippet in
 * the shared Caddyfile — `/assets/js/x.js` proxies to the error tracker's `/x.js`,
 * and `POST /x/e` is the beacon. Same-origin is not a convenience here: that
 * endpoint carries no CORS headers at all, deliberately, so a beacon that appears
 * to fail with a CORS error means the site block is missing `import errors` and
 * never that a header is missing. `connect-src` is already `'self'` on all six
 * apps, so nothing in the policy builder needs changing.
 *
 * BOTH tags carry the nonce, including the external one. The policy built in
 * `go/internal/core/spa` uses `'strict-dynamic'`, which makes browsers ignore `script-src 'self'` and
 * every allowlisted host — so a same-origin `src=` script is blocked without one.
 * Note the liff apps run the policy `reportOnly`, so a nonce mistake there is
 * console-only and silent; on admin it is enforced and the tag simply dies. Test
 * the admin side.
 *
 * The inline bootstrap MUST come first: it queues errors thrown while the deferred
 * x.js is still loading, and x.js drains `window.__xq` on startup. Reverse the
 * order and every error during page parse is lost, silently — /x/e answers 204 to
 * everything, so nothing on the server can tell you it happened.
 *
 * Shared here rather than copied into each vertical's layout for the same reason
 * `createProxy` and `Analytics` are shared: three apps, one policy, and a change
 * that reaches only two of them is worse than no change at all.
 *
 * Two deliberate differences from `Analytics`, which is otherwise this file's
 * template:
 *
 *   1. The key arrives as a PROP rather than being read from process.env here.
 *      One analytics id covers a whole vertical; error reporting does not — liff
 *      and admin are separate origins with separate projects and separate public
 *      keys, and compose hands BOTH services the same .env via `env_file`. So the
 *      call site has to name which variable it wants.
 *   2. There is no `APP_ENV === "local"` bail. Analytics deliberately keeps the
 *      laptop out of the data; the error tracker deliberately runs on both hosts,
 *      and the environment rides along in data-v so an alert can say which one.
 *
 * The key is read at REQUEST time in a Server Component, from the container env.
 * Rotating it is a container recreate, not a rebuild — do not move it to a
 * NEXT_PUBLIC_ variable, which webpack would inline at build time and freeze.
 *
 * Rendering nothing when the key is unset is what lets this ship dormant: the tag
 * lands in every app first and activates one origin at a time as its project and
 * key are provisioned.
 */
import { runtimeConfig } from "./runtime-config";

export function ErrorTracker({
  nonce,
  appKey,
}: {
  nonce?: string;
  appKey?: string;
}) {
  if (!appKey) return null;

  return (
    <>
      <script
        nonce={nonce}
        // A fixed literal with no interpolation, so there is no injection surface
        // here; appKey goes in as an attribute below, where React escapes it.
        dangerouslySetInnerHTML={{
          __html:
            'window.__xq=[];addEventListener("error",function(e){if(window.__xq)window.__xq.push(e)},true);' +
            'addEventListener("unhandledrejection",function(e){if(window.__xq)window.__xq.push(e)})',
        }}
      />
      <script
        nonce={nonce}
        defer
        src="/assets/js/x.js"
        data-k={appKey}
        data-v={runtimeConfig.appEnv ?? process.env.APP_ENV}
      />
    </>
  );
}
